import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config/api';

export const fetchReviews = createAsyncThunk(
  'reviews/fetchReviews',
  async ({ productId, page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      if (!productId) {
        return rejectWithValue('Product ID is required');
      }
      console.log('Fetching reviews for product:', productId);
      const response = await axios.get(`${API_URL}/api/public/reviews/${productId}?page=${page}&limit=${limit}`);
      console.log('Reviews fetched:', { count: response.data.data?.length, total: response.data.summary?.totalReviews });
      return response.data;
    } catch (error) {
      console.error('Fetch reviews error:', error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch reviews'
      );
    }
  }
);

export const submitReview = createAsyncThunk(
  'reviews/submitReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      console.log('Submitting review:', { productId: reviewData.productId, userName: reviewData.userName });
      const response = await axios.post(`${API_URL}/api/public/reviews`, reviewData);
      console.log('Review submission response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Review submission error:', error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Failed to submit review'
      );
    }
  }
);

export const fetchRecentReviews = createAsyncThunk(
  'reviews/fetchRecentReviews',
  async ({ limit = 6 } = {}, { rejectWithValue }) => {
    try {
      console.log('Fetching recent reviews, limit:', limit);
      const response = await axios.get(`${API_URL}/api/public/reviews?limit=${limit}`);
      console.log('Recent reviews fetched:', { count: response.data.data?.length });
      return response.data;
    } catch (error) {
      console.error('Fetch recent reviews error:', error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch recent reviews'
      );
    }
  }
);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState: {
    items: [],
    recentReviews: [],
    summary: {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    },
    pagination: {
      page: 1,
      totalPages: 1,
      total: 0,
      limit: 10
    },
    loading: false,
    submitting: false,
    error: null,
  },
  reducers: {
    clearReviews: (state) => {
      state.items = [];
      state.summary = {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      };
      state.pagination = {
        page: 1,
        totalPages: 1,
        total: 0,
        limit: 10
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.summary = action.payload.summary || state.summary;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(submitReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitReview.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      .addCase(fetchRecentReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.recentReviews = action.payload.data || [];
      })
      .addCase(fetchRecentReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.recentReviews = [];
      });
  },
});

export const { clearReviews } = reviewsSlice.actions;
export default reviewsSlice.reducer;

